import React, { useState, useContext } from 'react';
import * as XLSX from 'xlsx';
import mammoth from 'mammoth';
import moment from 'moment';
import { Button, Title } from '@mantine/core';
import AuthContext from '../AuthContext';
import { Document, Page, pdfjs } from 'react-pdf';
// Set the workerSrc globally

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@4.8.69/+esm`;

const Documentation = () => {
  const [textContent, setTextContent] = useState(''); // Store extracted text content
  const [tableData, setTableData] = useState([[]]); // Store table data for XLSX files
  const [pdfFile, setPdfFile] = useState(null); // Store the PDF file for preview
  const [lastUpdated, setLastUpdated] = useState(null); // Store last updated timestamp
  const { user } = useContext(AuthContext); // Access user context
  const [columns, setColumns] = useState([]); // Store column headers for XLSX files

  // Handle file upload event
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const fileExtension = file.name.split('.').pop().toLowerCase();
    resetState();

    switch (fileExtension) {
      case 'docx':
        readDocxFile(file);
        break;
      case 'xlsx':
        readXlsxFile(file);
        break;
      case 'pdf':
        setPdfFile(URL.createObjectURL(file));
        readPdfFile(file); // Read PDF content
        break;
      default:
        alert('Unsupported file type. Please upload a .docx, .xlsx, or .pdf file.');
    }
  };

  // Reset all states
  const resetState = () => {
    setTextContent('');
    setTableData([[]]);
    setPdfFile(null);
    setLastUpdated(null);
    setColumns([]);
  };

  // Read .docx file and extract text
  const readDocxFile = (file) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      const arrayBuffer = e.target.result;
      const { value } = await mammoth.extractRawText({ arrayBuffer });
      setTextContent(value);
    };
    reader.readAsArrayBuffer(file);
  };

  // Read .xlsx file and extract table data
  const readXlsxFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const binaryStr = e.target.result;
      const workbook = XLSX.read(binaryStr, { type: 'binary' });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
      setTableData(data);

      const headers = data[0] || [];
      setColumns(headers);
    };
    reader.readAsBinaryString(file);
  };

  // Read PDF file and extract text
  const readPdfFile = async (file) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      const typedArray = new Uint8Array(e.target.result);
      const pdfDoc = await pdfjsLib.getDocument(typedArray).promise; // Accessing getDocument from pdfjsLib
      let pdfText = '';

      for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum++) {
        const page = await pdfDoc.getPage(pageNum);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map((item) => item.str).join(' ');
        pdfText += `Page ${pageNum}: ${pageText}\n\n`;
      }

      setTextContent(pdfText);
    };
    reader.readAsArrayBuffer(file);
  };

  // Handle changes in table data
  const handleTableChange = (rowIndex, columnId, value) => {
    const newTableData = [...tableData];
    newTableData[rowIndex][columnId] = value;
    setTableData(newTableData);
    updateTimestamp();
  };

  // Add a new row to the table
  const addRow = () => {
    const newRow = new Array(columns.length).fill('');
    setTableData((prev) => [...prev, newRow]);
    updateTimestamp();
  };

  // Delete a row from the table
  const deleteRow = (rowIndex) => {
    const newTableData = tableData.filter((_, index) => index !== rowIndex);
    setTableData(newTableData);
    updateTimestamp();
  };

  // Update the timestamp when data changes
  const updateTimestamp = () => {
    setLastUpdated(moment().fromNow());
  };

  // Handle text change in the extracted content
  const handleTextChange = (e) => {
    setTextContent(e.target.value);
  };

  return (
    <div className="p-4">
      <Title order={1}>Documentation</Title>
      <input
        type="file"
        accept=".docx,.xlsx,.pdf"
        onChange={handleFileUpload}
        className="mb-4 border border-gray-300 p-2 rounded"
      />

      {textContent && (
        <div className="mt-4">
          <Title order={2}>Extracted Text:</Title>
          <textarea
            value={textContent}
            onChange={handleTextChange}
            className="border border-gray-300 p-2 w-full h-96 text-black"
          />
        </div>
      )}

      {pdfFile && (
        <div className="mt-4" style={{ height: '500px' }}>
          <Title order={2}>PDF Preview:</Title>
          <Document
            file={pdfFile}
            onLoadError={(error) => {
              console.error('Error loading PDF:', error);
              alert('There was an error loading the PDF document.');
            }}
          >
            <Page pageNumber={1} />
          </Document>
        </div>
      )}
    </div>
  );
};

export default Documentation;
